/**
 * v3.18-1297 — Ghost Task Detail Panel.
 *
 * Slide-out panel that opens inline when an operator clicks a Ghost
 * task row. Replaces the previous "navigate away to client page"
 * behavior — operators now stay in the Ghost dock while reading
 * complete notes, client info, communication history, timeline, and
 * navigating through their queue.
 *
 * Persisted UX state (localStorage):
 *   • ptc.gtdp.width      — panel width in px
 *   • ptc.gtdp.sections   — expand/collapse per section
 *   • ptc.gtdp.scrollY    — scroll offset within the panel
 *
 * Props:
 *   item          currently-focused ghost item
 *   items         ordered flat list for prev/next
 *   onClose       ()  =>
 *   onNavigate    (item) =>       — switch to a sibling item
 *   onEditNotes   (item, notes) => // returns { ok, item }
 *   onGoToClient  (item) =>       // explicit "open full client page"
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  X, ChevronLeft, ChevronRight, User, Building2, StickyNote,
  History, PhoneCall, Save, Edit3, ExternalLink, Clock,
  MessageSquare, Mail, CalendarPlus, ClipboardList, Users, Bell, CheckCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import useLiveActivityClaim from '../hooks/useLiveActivityClaim';   // v3.18-1343 LAC-VERIFY
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import ClientLocalTime from './ClientLocalTime';
import ClientCommunicationsTab from './ClientCommunicationsTab';
import { dispatchDial, dispatchCompose } from '../lib/comm_bus';
import { useNavigate } from 'react-router-dom';

const WIDTH_KEY = 'ptc.gtdp.width';
const SECTIONS_KEY = 'ptc.gtdp.sections';
const SCROLL_KEY = 'ptc.gtdp.scrollY';

const DEFAULT_SECTIONS = {
  notes: true,
  client: true,
  comms: true,
  timeline: true,
};

const clampWidth = (w) => Math.min(720, Math.max(360, Math.round(w)));

const loadWidth = () => {
  try {
    const raw = localStorage.getItem(WIDTH_KEY);
    if (raw) return clampWidth(parseInt(raw, 10) || 480);
  } catch (_e) { /* noop */ }
  return 480;
};

const loadSections = () => {
  try {
    const raw = localStorage.getItem(SECTIONS_KEY);
    if (raw) return { ...DEFAULT_SECTIONS, ...JSON.parse(raw) };
  } catch (_e) { /* noop */ }
  return DEFAULT_SECTIONS;
};

const SectionHeader = ({ label, icon: Icon, open, onToggle, testId }) => (
  <button
    type="button"
    onClick={onToggle}
    data-testid={testId}
    className="w-full flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider
               text-slate-500 hover:text-slate-800 mt-4 mb-1 px-1"
  >
    <Icon className="h-3.5 w-3.5" />
    <span className="flex-1 text-left">{label}</span>
    <ChevronRight
      className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-90' : ''}`}
    />
  </button>
);

const GhostTaskDetailPanel = ({
  item, items, onClose, onNavigate, onEditNotes, onGoToClient,
}) => {
  const [width, setWidth] = useState(loadWidth);
  const [sections, setSections] = useState(loadSections);
  const [editingNotes, setEditingNotes] = useState(false);
  const [draftNotes, setDraftNotes] = useState(item?.description || '');
  const [savingNotes, setSavingNotes] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const bodyRef = useRef(null);
  const resizing = useRef(false);

  // v3.18-1343 LAC-VERIFY — a follow-up task (kind === 'follow_up')
  // maps to the completing_follow_up activity so peers see who is
  // working the item; every other Ghost kind (task, general) claims
  // ghost_workflow as a generic operator-presence marker.
  const _lacWorkflow = (item?.kind === 'follow_up')
    ? 'completing_follow_up'
    : 'ghost_workflow';
  useLiveActivityClaim({
    clientId: item?.client_id || null,
    workflow: _lacWorkflow,
    metadata: { ghost_item_id: item?.id || null,
                 kind:          item?.kind    || null },
    enabled:  !!(item && item.client_id),
  });

  useEffect(() => { try { localStorage.setItem(WIDTH_KEY, String(width)); } catch (_e) { /* noop */ } }, [width]);
  useEffect(() => { try { localStorage.setItem(SECTIONS_KEY, JSON.stringify(sections)); } catch (_e) { /* noop */ } }, [sections]);

  // Restore scroll offset when the panel first opens / item changes.
  useEffect(() => {
    if (!bodyRef.current) return;
    try {
      const y = parseInt(localStorage.getItem(SCROLL_KEY) || '0', 10) || 0;
      bodyRef.current.scrollTop = y;
    } catch (_e) { /* noop */ }
  }, [item?.id]);

  const handleScroll = (e) => {
    try { localStorage.setItem(SCROLL_KEY, String(e.target.scrollTop || 0)); } catch (_ee) { /* noop */ }
  };

  useEffect(() => {
    setDraftNotes(item?.description || '');
    setEditingNotes(false);
  }, [item?.id]);

  const currentIndex = useMemo(() => {
    if (!items?.length || !item?.id) return -1;
    return items.findIndex((x) => x.id === item.id);
  }, [items, item?.id]);
  const prevItem = currentIndex > 0 ? items[currentIndex - 1] : null;
  const nextItem = currentIndex >= 0 && currentIndex < items.length - 1 ? items[currentIndex + 1] : null;

  // Timeline fetch — best-effort against /api/clients/{id}/timeline OR /api/activities?client_id=…
  useEffect(() => {
    if (!item?.client_id || !sections.timeline) return;
    let cancelled = false;
    setTimelineLoading(true);
    (async () => {
      try {
        let rows = [];
        try {
          const { data } = await api.get(`/clients/${item.client_id}/timeline`, { params: { limit: 30 } });
          rows = data?.events || data?.timeline || data || [];
        } catch (_e) {
          try {
            const { data } = await api.get('/activities', { params: { client_id: item.client_id, limit: 30 } });
            rows = data?.activities || data?.items || data || [];
          } catch (_e2) { rows = []; }
        }
        if (!cancelled) setTimeline(Array.isArray(rows) ? rows.slice(0, 30) : []);
      } finally {
        if (!cancelled) setTimelineLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [item?.client_id, sections.timeline]);

  // Keyboard: Esc closes, arrows navigate.
  useEffect(() => {
    if (!item) return;
    const onKey = (e) => {
      if (e.target?.tagName === 'TEXTAREA' || e.target?.tagName === 'INPUT') return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowUp' && prevItem)   { e.preventDefault(); onNavigate(prevItem); }
      if (e.key === 'ArrowDown' && nextItem) { e.preventDefault(); onNavigate(nextItem); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [item, prevItem, nextItem, onClose, onNavigate]);

  // Drag-resize.
  const startResize = (e) => {
    resizing.current = { startX: e.clientX, startW: width };
    document.body.style.cursor = 'ew-resize';
  };
  useEffect(() => {
    const onMove = (e) => {
      if (!resizing.current) return;
      const { startX, startW } = resizing.current;
      // Panel sits to the LEFT of the dock; grow when moving mouse LEFT.
      setWidth(clampWidth(startW + (startX - e.clientX)));
    };
    const onUp = () => {
      if (!resizing.current) return;
      resizing.current = null;
      document.body.style.cursor = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const result = await onEditNotes?.(item, draftNotes);
      if (result?.ok) {
        toast.success('Notes updated');
        setEditingNotes(false);
      } else if (result && !result.ok) {
        toast.error(result.error || 'Failed to update notes');
      } else {
        setEditingNotes(false);
      }
    } finally {
      setSavingNotes(false);
    }
  };

  if (!item) return null;

  return (
    <aside
      data-testid="ghost-task-detail-panel"
      style={{ width: `${width}px`, right: '256px' }}
      className="hidden xl:flex fixed inset-y-0 z-50 bg-white border-l border-slate-200 shadow-2xl flex-col"
    >
      {/* Drag handle */}
      <div
        onMouseDown={startResize}
        title="Drag to resize"
        className="absolute inset-y-0 left-0 w-1.5 cursor-ew-resize hover:bg-indigo-400/50 transition"
        data-testid="ghost-task-detail-resize"
      />

      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200">
        <button
          type="button"
          onClick={() => prevItem && onNavigate(prevItem)}
          disabled={!prevItem}
          data-testid="ghost-task-detail-prev"
          className="h-7 w-7 rounded flex items-center justify-center text-slate-500 hover:bg-slate-100 disabled:opacity-30"
          title="Previous (↑)"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => nextItem && onNavigate(nextItem)}
          disabled={!nextItem}
          data-testid="ghost-task-detail-next"
          className="h-7 w-7 rounded flex items-center justify-center text-slate-500 hover:bg-slate-100 disabled:opacity-30"
          title="Next (↓)"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
        <div className="flex-1 min-w-0 px-1">
          <div className="text-xs text-slate-400 uppercase tracking-wider">
            Ghost Task {currentIndex >= 0 && `· ${currentIndex + 1} of ${items.length}`}
          </div>
          <div className="text-sm font-semibold text-slate-900 truncate" title={item.title}>
            {item.title}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="ghost-task-detail-close"
          className="h-7 w-7 rounded flex items-center justify-center text-slate-500 hover:bg-slate-100"
          title="Close (Esc)"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Body */}
      <div
        ref={bodyRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-4 py-3"
      >
        {/* Quiet hours banner (client-local-time) */}
        {item.client_id && (
          <ClientLocalTime clientId={item.client_id} variant="banner" />
        )}

        {/* Client / Company row */}
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {item.client_name && (
            <span className="inline-flex items-center gap-1 text-xs text-slate-700 bg-slate-100 rounded px-2 py-0.5">
              <User className="h-3 w-3" /> {item.client_name}
            </span>
          )}
          {item.client_company && (
            <span className="inline-flex items-center gap-1 text-xs text-slate-600 bg-slate-50 rounded px-2 py-0.5">
              <Building2 className="h-3 w-3" /> {item.client_company}
            </span>
          )}
          {item.due_at && (
            <span className="inline-flex items-center gap-1 text-xs text-slate-500">
              <Clock className="h-3 w-3" /> due {new Date(item.due_at).toLocaleString()}
            </span>
          )}
        </div>
        {item.client_id && (
          <div className="mt-2">
            <Button
              size="sm" variant="outline"
              onClick={() => onGoToClient?.(item)}
              data-testid="ghost-task-detail-open-client"
              className="text-xs h-7"
            >
              <ExternalLink className="h-3 w-3 mr-1" /> Open full client page
            </Button>
          </div>
        )}

        {/* v3.18-1299 — Inline action bar. Every common comms task
            launches without leaving Ghost. */}
        <GhostActionBar item={item} />

        {/* v3.18-1299 — Notifications tied to this client surface
            inline so operators see every ring/SMS/email while they work
            the task. */}
        {item.client_id && (
          <GhostRelatedNotifications clientId={item.client_id} />
        )}

        {/* Notes */}
        <SectionHeader
          label="Complete notes"
          icon={StickyNote}
          open={sections.notes}
          onToggle={() => setSections((s) => ({ ...s, notes: !s.notes }))}
          testId="ghost-task-detail-toggle-notes"
        />
        {sections.notes && (
          <div className="rounded border border-slate-200 bg-slate-50 p-2">
            {editingNotes ? (
              <>
                <Textarea
                  value={draftNotes}
                  onChange={(e) => setDraftNotes(e.target.value)}
                  rows={6}
                  data-testid="ghost-task-detail-notes-textarea"
                  className="bg-white"
                />
                <div className="flex justify-end gap-1 mt-1.5">
                  <Button size="sm" variant="ghost" onClick={() => { setEditingNotes(false); setDraftNotes(item.description || ''); }}>
                    Cancel
                  </Button>
                  <Button
                    size="sm" onClick={saveNotes} disabled={savingNotes}
                    data-testid="ghost-task-detail-notes-save"
                  >
                    <Save className="h-3 w-3 mr-1" />
                    {savingNotes ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="whitespace-pre-wrap text-sm text-slate-800 leading-snug" data-testid="ghost-task-detail-notes">
                  {item.description ? item.description : (
                    <span className="text-slate-400 italic">No notes attached to this task.</span>
                  )}
                </div>
                {typeof onEditNotes === 'function' && (
                  <div className="flex justify-end mt-1.5">
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => setEditingNotes(true)}
                      data-testid="ghost-task-detail-notes-edit"
                      className="h-7 text-xs"
                    >
                      <Edit3 className="h-3 w-3 mr-1" /> Edit
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Client details */}
        {item.client_id && (
          <>
            <SectionHeader
              label="Client information"
              icon={User}
              open={sections.client}
              onToggle={() => setSections((s) => ({ ...s, client: !s.client }))}
              testId="ghost-task-detail-toggle-client"
            />
            {sections.client && (
              <ClientInfoBlock clientId={item.client_id} />
            )}
          </>
        )}

        {/* Communications */}
        {item.client_id && (
          <>
            <SectionHeader
              label="Communications"
              icon={PhoneCall}
              open={sections.comms}
              onToggle={() => setSections((s) => ({ ...s, comms: !s.comms }))}
              testId="ghost-task-detail-toggle-comms"
            />
            {sections.comms && (
              <ClientCommunicationsTab
                clientId={item.client_id}
                companyId={item.client_company_id || item.company_id}
                canWrite
                compact
              />
            )}
          </>
        )}

        {/* Timeline */}
        {item.client_id && (
          <>
            <SectionHeader
              label="Timeline"
              icon={History}
              open={sections.timeline}
              onToggle={() => setSections((s) => ({ ...s, timeline: !s.timeline }))}
              testId="ghost-task-detail-toggle-timeline"
            />
            {sections.timeline && (
              <div className="text-xs text-slate-600 space-y-1.5">
                {timelineLoading ? (
                  <div className="text-slate-400 py-2">Loading…</div>
                ) : timeline.length === 0 ? (
                  <div className="text-slate-400 py-2" data-testid="ghost-task-detail-timeline-empty">
                    No recent activity for this client.
                  </div>
                ) : (
                  timeline.map((ev, i) => (
                    <div
                      key={ev.id || i}
                      data-testid={`ghost-task-detail-timeline-${i}`}
                      className="border-l-2 border-slate-200 pl-2 py-0.5"
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-700">
                          {(ev.type || ev.action || 'event').replaceAll('_', ' ')}
                        </span>
                        {ev.timestamp && (
                          <span className="text-[10px] text-slate-400">
                            {new Date(ev.timestamp).toLocaleString()}
                          </span>
                        )}
                      </div>
                      {(ev.summary || ev.description || ev.body) && (
                        <div className="text-slate-500 truncate">
                          {ev.summary || ev.description || ev.body}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>
    </aside>
  );
};

// Lightweight client-info block. Reuses /api/clients/{id}.
const ClientInfoBlock = ({ clientId }) => {
  const [client, setClient] = useState(null);
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/clients/${clientId}`);
        if (!cancelled) setClient(data);
      } catch (_e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [clientId]);
  if (!client) return <div className="text-xs text-slate-400 py-1">Loading client…</div>;
  const rows = [
    ['Name', client.full_name],
    ['Company', client.company_name || client.company],
    ['Email', client.email],
    ['Phone', client.mobile_phone || client.phone],
    ['Status', client.status || client.stage],
  ].filter(([, v]) => v);
  return (
    <div className="text-xs text-slate-700 space-y-1">
      {rows.map(([label, val]) => (
        <div key={label} className="flex gap-2">
          <span className="text-slate-400 uppercase text-[10px] w-16 tracking-wider">{label}</span>
          <span className="flex-1 truncate" title={val}>{val}</span>
        </div>
      ))}
    </div>
  );
};

export default GhostTaskDetailPanel;

// ── v3.18-1299 Ghost inline action bar ────────────────────────────
// v3.18-1300 — added one-click follow-up templates.

const FOLLOWUP_TEMPLATES = [
  { key: 'call_back_tomorrow',
    label: 'Call back tomorrow', days: 1,
    title: 'Call back — tomorrow',
    body: 'Client needs a follow-up call tomorrow.',
    draft_subject: 'Following up',
    draft_body: 'Hi {name},\n\nJust a quick note — I\'ll be giving you a call tomorrow to continue where we left off.\n\nBest,\n{rep}' },
  { key: 'call_back_2_days',
    label: 'Call back in 2 days', days: 2,
    title: 'Call back — 2 days',
    body: 'Client asked for a call back in two business days.',
    draft_subject: 'Following up in a couple of days',
    draft_body: 'Hi {name},\n\nI\'ll circle back in a couple of days to keep things moving.\n\nBest,\n{rep}' },
  { key: 'send_documents',
    label: 'Send documents reminder', days: 1,
    title: 'Send documents reminder',
    body: 'Send a reminder for the outstanding documents.',
    draft_subject: 'Quick reminder — documents',
    draft_body: 'Hi {name},\n\nWhen you get a moment, could you send over the remaining documents so I can finalize?\n\nThanks,\n{rep}' },
  { key: 'waiting_customer',
    label: 'Waiting on customer', days: 3,
    title: 'Waiting on customer',
    body: 'Awaiting response from the customer.' },
  { key: 'waiting_partner',
    label: 'Waiting on partner', days: 3,
    title: 'Waiting on partner',
    body: 'Awaiting response from the funding/partner side.' },
  { key: 'escalate_manager',
    label: 'Escalate to manager', days: 0,
    title: 'Escalate to manager',
    body: 'Escalation requested — needs manager attention.',
    priority: 'high' },
  { key: 'final_push',
    label: 'Final push', days: 1,
    title: 'Final push',
    body: 'Last attempt to close before archiving.',
    priority: 'high',
    draft_subject: 'One last check-in',
    draft_body: 'Hi {name},\n\nI wanted to reach out one last time before I close things out. Let me know if you\'d still like to move forward.\n\nBest,\n{rep}' },
  { key: 'no_response',
    label: 'No response', days: 7,
    title: 'No response — check in',
    body: 'No response so far. Retry in a week.' },
];

const GhostActionBar = ({ item }) => {
  const navigate = useNavigate();
  const clientId = item?.client_id;
  const goToDirectory = () => navigate('/contact-directory');
  const call = async () => {
    if (!clientId) return;
    try {
      const { data } = await api.get(`/clients/${clientId}`);
      const num = data?.mobile_phone || data?.phone;
      if (num) dispatchDial({ number: num, client_id: clientId });
      else toast.info('No phone number on record for this client.');
    } catch (_e) { toast.error('Could not load client phone.'); }
  };
  const sms = () => call();
  const email = async () => {
    if (!clientId) return;
    try {
      const { data } = await api.get(`/clients/${clientId}`);
      const addr = data?.email;
      if (addr) dispatchCompose({ to: addr, client_id: clientId });
      else toast.info('No email on record for this client.');
    } catch (_e) { toast.error('Could not load client email.'); }
  };
  const scheduleAppointment = () => {
    if (clientId) navigate(`/calendar?client_id=${clientId}`);
    else navigate('/calendar');
  };
  const addFollowUp = async () => {
    if (!clientId) { toast.info('Attach a client first.'); return; }
    const notes = window.prompt('Follow-up note:');
    if (notes === null) return;
    try {
      await api.post('/ghost-items', {
        title: `Follow-up: ${item.title}`,
        description: notes.trim() || 'Follow-up scheduled from Ghost detail panel',
        priority: 'normal',
        client_id: clientId,
        due_at: new Date(Date.now() + 24 * 3600 * 1000).toISOString(),
      });
      toast.success('Follow-up created');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to create follow-up'); }
  };
  const createTask = async () => {
    const title = window.prompt('New task title:');
    if (!title || !title.trim()) return;
    try {
      await api.post('/ghost-items', {
        title: title.trim(),
        description: '',
        priority: 'normal',
        client_id: clientId || null,
      });
      toast.success('Task created');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to create task'); }
  };

  // v3.18-1300 — one-click follow-up templates.
  const applyTemplate = async (tpl) => {
    const dueIso = new Date(Date.now() + (tpl.days || 0) * 86400_000).toISOString();
    try {
      await api.post('/ghost-items', {
        title: tpl.title,
        description: tpl.body,
        priority: tpl.priority || 'normal',
        client_id: clientId || null,
        due_at: dueIso,
        // v3.18-1301 — tag the created item with the template key so
        // Manager Dashboard can compute conversion / completion metrics.
        template_key: tpl.key,
        template_label: tpl.label,
      });
      toast.success(`Follow-up “${tpl.label}” created`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create follow-up');
      return;
    }
    // Draft an email in Universal Compose when the template ships one.
    if (tpl.draft_subject && clientId) {
      try {
        const { data } = await api.get(`/clients/${clientId}`);
        const clientName = data?.full_name?.split(' ')[0] || 'there';
        const repName = data?.sales_rep_1_name || '';
        const email = data?.email;
        if (email) {
          dispatchCompose({
            to: email,
            client_id: clientId,
            subject: tpl.draft_subject,
            body: (tpl.draft_body || '')
              .replaceAll('{name}', clientName)
              .replaceAll('{rep}', repName),
          });
        }
      } catch (_e) { /* silent */ }
    }
  };

  const btn = 'inline-flex items-center gap-1 rounded-md text-[11px] font-medium px-2 py-1 border border-slate-200 hover:bg-slate-50 transition';
  return (
    <div className="mt-3 space-y-2" data-testid="ghost-task-detail-action-bar">
      <div className="flex flex-wrap gap-1.5">
        <button type="button" onClick={call} disabled={!clientId} className={btn} data-testid="ghost-action-call">
          <PhoneCall className="h-3 w-3 text-emerald-600" /> Call
        </button>
        <button type="button" onClick={sms} disabled={!clientId} className={btn} data-testid="ghost-action-sms">
          <MessageSquare className="h-3 w-3 text-blue-600" /> SMS
        </button>
        <button type="button" onClick={email} disabled={!clientId} className={btn} data-testid="ghost-action-email">
          <Mail className="h-3 w-3 text-indigo-600" /> Email
        </button>
        <button type="button" onClick={scheduleAppointment} className={btn} data-testid="ghost-action-appointment">
          <CalendarPlus className="h-3 w-3 text-purple-600" /> Appointment
        </button>
        <button type="button" onClick={addFollowUp} className={btn} data-testid="ghost-action-followup">
          <Clock className="h-3 w-3 text-amber-600" /> Follow-up
        </button>
        <button type="button" onClick={createTask} className={btn} data-testid="ghost-action-task">
          <ClipboardList className="h-3 w-3 text-slate-600" /> Task
        </button>
        <button type="button" onClick={goToDirectory} className={btn} data-testid="ghost-action-directory">
          <Users className="h-3 w-3 text-slate-600" /> Directory
        </button>
      </div>
      {/* v3.18-1300 One-click follow-up templates */}
      <div className="flex flex-wrap gap-1" data-testid="ghost-followup-templates">
        <span className="text-[10px] uppercase tracking-wider text-slate-400 self-center pr-1">
          Templates:
        </span>
        {FOLLOWUP_TEMPLATES.map((tpl) => (
          <button
            key={tpl.key}
            type="button"
            onClick={() => applyTemplate(tpl)}
            className="text-[10px] px-2 py-0.5 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition"
            data-testid={`ghost-followup-template-${tpl.key}`}
          >
            {tpl.label}
          </button>
        ))}
      </div>
    </div>
  );
};

// ── v3.18-1299 Ghost-related notifications ────────────────────────
// Surfaces every notification tied to the current client so operators
// see phone/SMS/email activity while they work the task.
const GhostRelatedNotifications = ({ clientId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/notifications', { params: { limit: 20 } });
      const all = data?.notifications || [];
      setRows(all.filter((n) => n.client_id === clientId).slice(0, 8));
    } catch (_e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const dismiss = async (id, e) => {
    e?.stopPropagation();
    try { await api.patch(`/notifications/${id}/read`); } catch (_e) { /* noop */ }
    setRows((r) => r.filter((x) => x.id !== id));
  };

  if (loading) return null;
  if (rows.length === 0) return null;

  return (
    <div className="mt-3 border border-indigo-200 rounded-md bg-indigo-50/40 p-2" data-testid="ghost-task-detail-related-notifications">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-indigo-700 mb-1.5">
        <Bell className="h-3 w-3" />
        Related notifications for this client
        <span className="ml-1 text-slate-500 normal-case tracking-normal font-normal">
          ({rows.length})
        </span>
      </div>
      <div className="space-y-1">
        {rows.map((n) => (
          <div
            key={n.id}
            className="flex items-start gap-2 text-[11px] bg-white border border-indigo-100 rounded px-2 py-1"
            data-testid={`ghost-task-detail-notif-${n.id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-800 truncate">{n.title}</div>
              {n.body && <div className="text-slate-500 truncate">{n.body}</div>}
              {n.created_at && (
                <div className="text-[10px] text-slate-400">
                  {new Date(n.created_at).toLocaleString()}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={(e) => dismiss(n.id, e)}
              className="text-slate-400 hover:text-slate-700"
              title="Mark read"
              data-testid={`ghost-task-detail-notif-dismiss-${n.id}`}
            >
              <CheckCheck className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
